import AppConfig from '../appConfig';

// Esri World Imagery as the satellite basemap. Inert until Rails hands the page a
// key through configureEsriSatellite. Esri bills per session, so one is started
// only when satellite is shown and is reused across toggles and reloads.

const BASEMAP_STYLES_URL = 'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2';
const SESSION_DURATION_SECONDS = 43200; // 12 hours, the service maximum
const EXPIRY_MARGIN_MS = 10 * 60 * 1000;
const DEFAULT_TILE_SIZE = 256;

export const ESRI_SATELLITE_CREDIT_PREFIX = 'Satellite imagery: powered by ';
export const ESRI_SATELLITE_CREDIT_LINK_TEXT = 'Esri';
export const ESRI_SATELLITE_CREDIT_URL = 'https://www.esri.com';
const ESRI_SATELLITE_CREDIT = `${ESRI_SATELLITE_CREDIT_PREFIX}${ESRI_SATELLITE_CREDIT_LINK_TEXT}`;

let apiKey;
let apiKeyHash;
let cached; // { endTime, keyHash, source }
let inflight;

const storageKey = () => `${AppConfig.constants.environment}-EsriSatelliteSession`;

// djb2, so a stored session is only reused by the key that started it
function hashKey(key) {
    let hash = 5381;
    for (let i = 0; i < key.length; i += 1) hash = ((hash * 33) ^ key.charCodeAt(i)) >>> 0;
    return hash.toString(36);
}

// A stored source goes straight into the initial style, where one invalid key
// fails validation for the whole map.
const isUsableSource = source =>
    !!source &&
    source.type === 'raster' &&
    Array.isArray(source.tiles) &&
    source.tiles.length > 0 &&
    !source.url;

export function isEsriSatelliteExpiring(endTime) {
    return !(typeof endTime === 'number' && endTime - EXPIRY_MARGIN_MS > Date.now());
}

const isFresh = entry =>
    !!entry &&
    entry.keyHash === apiKeyHash &&
    isUsableSource(entry.source) &&
    !isEsriSatelliteExpiring(entry.endTime);

const toResult = entry => ({ source: { ...entry.source }, endTime: entry.endTime });

function readStored() {
    try {
        const raw = window.localStorage.getItem(storageKey());
        return raw ? JSON.parse(raw) : undefined;
    } catch (e) {
        return undefined;
    }
}

function writeStored(entry) {
    try {
        window.localStorage.setItem(storageKey(), JSON.stringify(entry));
    } catch (e) {
        // storage unavailable; the in-memory copy still works
    }
}

export function configureEsriSatellite(key) {
    apiKey = key || undefined;
    apiKeyHash = apiKey && hashKey(apiKey);
}

export function isEsriSatelliteEnabled() {
    return !!apiKey;
}

/** `{ source, endTime }` when a still-valid session is cached, synchronously. */
export function getCachedEsriSatellite() {
    if (!isEsriSatelliteEnabled()) return undefined;
    if (!isFresh(cached)) {
        const stored = readStored();
        cached = isFresh(stored) ? stored : undefined;
    }
    return cached && toResult(cached);
}

async function fetchJson(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Esri request failed with HTTP ${response.status}`);
    const json = await response.json();
    if (json && json.error)
        throw new Error(`Esri request failed: ${json.error.message || json.error.code}`);
    return json;
}

function pickImagerySource(style) {
    const sources = (style && style.sources) || {};
    const rasters = Object.keys(sources)
        .map(id => sources[id])
        .filter(s => s && s.type === 'raster');
    const imagery =
        rasters.find(s => JSON.stringify(s.tiles || s.url || '').indexOf('World_Imagery') !== -1) ||
        rasters[0];
    if (!imagery || !Array.isArray(imagery.tiles) || !imagery.tiles.length) {
        throw new Error('Esri imagery style has no raster source with tiles');
    }
    // Style-spec keys only: given `url` the renderer fetches it instead of `tiles`,
    // and it rejects the whole style over Esri's non-spec `copyrightText`.
    const source = {
        type: 'raster',
        tiles: imagery.tiles,
        tileSize: imagery.tileSize || DEFAULT_TILE_SIZE,
    };
    if (typeof imagery.minzoom === 'number') source.minzoom = imagery.minzoom;
    if (typeof imagery.maxzoom === 'number') source.maxzoom = imagery.maxzoom;
    if (imagery.attribution) source.attribution = imagery.attribution;
    return source;
}

async function startSession() {
    const session = await fetchJson(
        `${BASEMAP_STYLES_URL}/sessions/start?styleFamily=arcgis&durationSeconds=${SESSION_DURATION_SECONDS}` +
            `&token=${encodeURIComponent(apiKey)}`,
    );
    if (!session.sessionToken) throw new Error('Esri session response has no sessionToken');
    // the style's tile URLs carry the session token (echoToken defaults to true)
    const style = await fetchJson(
        `${BASEMAP_STYLES_URL}/styles/arcgis/imagery?token=${encodeURIComponent(session.sessionToken)}`,
    );
    cached = {
        endTime: Number(session.endTime),
        keyHash: apiKeyHash,
        source: pickImagerySource(style),
    };
    writeStored(cached);
    return toResult(cached);
}

/** Resolves to `{ source, endTime }`, starting a session only when none is cached. */
export function getEsriSatellite() {
    if (!isEsriSatelliteEnabled())
        return Promise.reject(new Error('Esri satellite is not enabled'));
    const esri = getCachedEsriSatellite();
    if (esri) return Promise.resolve(esri);
    if (!inflight) {
        const clear = () => {
            inflight = undefined;
        };
        inflight = startSession();
        inflight.then(clear, clear);
    }
    return inflight;
}

export function drawEsriSatelliteCreditIfVisible(mapViewers, context, height) {
    if (!mapViewers.some(mapViewer => mapViewer.isEsriSatelliteVisible)) return;
    const fontSize = Math.max(9, Math.round(height / 40));
    const padding = Math.round(fontSize / 2);
    context.save();
    context.font = `${fontSize}px "Source Sans Pro", sans-serif`;
    const textWidth = context.measureText(ESRI_SATELLITE_CREDIT).width;
    context.fillStyle = 'rgba(255, 255, 255, 0.8)';
    context.fillRect(
        0,
        height - fontSize - padding * 2,
        textWidth + padding * 2,
        fontSize + padding * 2,
    );
    context.fillStyle = 'rgba(0, 0, 0, 0.87)';
    context.textBaseline = 'bottom';
    context.fillText(ESRI_SATELLITE_CREDIT, padding, height - padding);
    context.restore();
}
